import {
  InviteItem,
  InviteItemReason,
  InviteItemResourceType,
  InviteItemStatus,
  InviteListItem,
  InviteStatus,
} from '../../../types';

export type InviteTone = 'success' | 'error' | 'warning' | 'neutral' | 'muted';

export const INVITE_STATUS_LABELS: Record<InviteStatus, string> = {
  pending: 'Pending',
  sent: 'Sent',
  not_sent: 'Not sent',
  accepted: 'Accepted',
  not_accepted: 'Not accepted',
  expired: 'Expired',
  automatically_accepted: 'Accepted automatically',
  partially_accepted: 'Partially accepted',
  cancelled: 'Replaced',
};

export const INVITE_ITEM_STATUS_LABELS: Record<InviteItemStatus, string> = {
  pending: 'Pending',
  sent: 'Sent',
  not_sent: 'Not sent',
  accepted: 'Accepted',
  auto_accepted: 'Accepted automatically',
  added: 'Added',
  already_in: 'Already in',
  failed: 'Failed',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

export const INVITE_ITEM_STATUS_TONES: Record<InviteItemStatus, InviteTone> = {
  pending: 'neutral',
  sent: 'neutral',
  not_sent: 'warning',
  accepted: 'success',
  auto_accepted: 'success',
  added: 'success',
  already_in: 'muted',
  failed: 'error',
  expired: 'error',
  cancelled: 'muted',
};

// legacy / unknown say nothing useful, so they are left out (and so is a null reason)
export const INVITE_ITEM_REASON_LABELS: Partial<Record<InviteItemReason, string>> = {
  new_account: 'New to Vyce',
  new_to_company: 'New to this company',
  new_to_module: 'New to this module',
  details_missing: 'Details missing',
  details_stale: 'Details not updated for 3 months',
  no_recent_clock_in: 'No clock-in for 3 months',
  no_payroll_record: 'No Payroll record',
  manual_resend: 'Resent by an admin',
};

export const INVITE_RESOURCE_TYPE_LABELS: Record<InviteItemResourceType, string> = {
  team: 'Team',
  location: 'Location',
  pay_schedule: 'Pay schedule',
};

const DELETED_RESOURCE_LABELS: Record<InviteItemResourceType, string> = {
  team: 'Deleted team',
  location: 'Deleted location',
  pay_schedule: 'Deleted pay schedule',
};

const PAY_SCHEME_LABELS: Record<string, string> = {
  cis: 'CIS',
  paye: 'PAYE',
  'self-employed': 'Self-employed',
  umbrella: 'Umbrella',
};

// Envelope statuses a Retry may run on: the invite is closed and at least one place failed
const RETRYABLE_INVITE_STATUSES: (string | null | undefined)[] = ['partially_accepted', 'not_accepted'];

// The invite was replaced; its open places moved to the newer invite
const SUPERSEDED_PREFIX = 'superseded by invite';

export const getPaySchemeLabel = (payScheme?: string | null) =>
  payScheme ? PAY_SCHEME_LABELS[payScheme] ?? payScheme : null;

/** Name as it was when invited; `null` from the server means the resource has since been deleted */
export const getInviteItemName = (item: InviteItem) =>
  item.resource_name?.trim() || DELETED_RESOURCE_LABELS[item.resource_type] || 'Deleted place';

export const getInviteItemFailureText = (item: InviteItem) => {
  const reason = item.failure_reason?.trim();
  if (!reason) {
    return null;
  }
  return reason.toLowerCase().startsWith(SUPERSEDED_PREFIX) ? 'Moved to a newer invite' : reason;
};

/** A row the invite dialog added straight away: nothing was sent, so there is no email or phone */
export const isDirectAddInvite = (invite: InviteListItem) => !invite.email && !invite.phone;

/** Replaced invites can't be resent (409), and a direct add was never sent in the first place */
export const canResendInvite = (invite: InviteListItem) =>
  invite.status !== 'cancelled' && !isDirectAddInvite(invite);

/**
 * Retry only makes sense on a failed place of a worker invite the person accepted, once the invite
 * is closed. Direct-add places are added again instead. The server answers 409 to everything else.
 */
export const canRetryInviteItem = (invite: InviteListItem, item: InviteItem) =>
  item.status === 'failed' &&
  item.source !== 'silent_add' &&
  invite.invite_type !== 'staff' &&
  !!invite.user_id &&
  RETRYABLE_INVITE_STATUSES.includes(invite.status);

/**
 * A Retry puts the line back to pending on a closed invite. A pending line on an open invite is
 * just waiting to be sent.
 */
export const isInviteItemRetrying = (invite: InviteListItem, item: InviteItem) =>
  item.status === 'pending' && RETRYABLE_INVITE_STATUSES.includes(invite.status);

/** What still needs attention on a place. Settled outcomes (accepted, added, sent…) say nothing here:
 * the onboarding state in the same card already tells that story. */
export interface InvitePlaceIssue {
  label: string;
  tone: InviteTone;
}

/** One team, location or pay schedule (+ scheme), however many lines it took — a location invited
 * on two shifts is still one place */
export interface InvitePlace {
  key: string;
  name: string;
  payScheme: string | null;
  shiftCount: number;
  lines: InviteItem[];
  issue: InvitePlaceIssue | null;
  /** Failed lines Retry can run on */
  retryable: InviteItem[];
}

export interface InvitePlaceGroups {
  teams: InvitePlace[];
  locations: InvitePlace[];
  paySchedules: InvitePlace[];
}

// Most pressing first
const ISSUE_STATUSES: InviteItemStatus[] = ['failed', 'not_sent', 'expired', 'cancelled'];

const getPlaceIssue = (invite: InviteListItem, lines: InviteItem[]): InvitePlaceIssue | null => {
  if (lines.some(line => isInviteItemRetrying(invite, line))) {
    return { label: 'Retrying…', tone: 'neutral' };
  }
  for (const status of ISSUE_STATUSES) {
    const line = lines.find(item => item.status === status);
    if (line) {
      const detail = getInviteItemFailureText(line);
      const label = INVITE_ITEM_STATUS_LABELS[status];
      return {
        label: status === 'cancelled' && detail ? detail : detail ? `${label} — ${detail}` : label,
        tone: INVITE_ITEM_STATUS_TONES[status],
      };
    }
  }
  return null;
};

export const groupInvitePlaces = (invite: InviteListItem): InvitePlaceGroups => {
  const byKey = new Map<string, { type: InviteItemResourceType; lines: InviteItem[] }>();
  (invite.items ?? []).forEach(item => {
    const key = `${item.resource_type}:${item.resource_id}:${
      item.resource_type === 'pay_schedule' ? item.pay_scheme ?? '' : ''
    }`;
    const entry = byKey.get(key) ?? { type: item.resource_type, lines: [] };
    entry.lines.push(item);
    byKey.set(key, entry);
  });

  const groups: InvitePlaceGroups = { teams: [], locations: [], paySchedules: [] };
  byKey.forEach(({ type, lines }, key) => {
    const [first] = lines;
    const place: InvitePlace = {
      key,
      name: getInviteItemName(first),
      payScheme: type === 'pay_schedule' ? getPaySchemeLabel(first.pay_scheme) : null,
      shiftCount: type === 'location' ? new Set(lines.map(line => line.shift_id).filter(Boolean)).size : 0,
      lines,
      issue: getPlaceIssue(invite, lines),
      retryable: lines.filter(line => canRetryInviteItem(invite, line)),
    };
    if (type === 'team') groups.teams.push(place);
    else if (type === 'location') groups.locations.push(place);
    else groups.paySchedules.push(place);
  });
  return groups;
};

const ITEM_MODULE_NAMES: Record<InviteItem['module'], string> = {
  teams: 'Teams',
  payroll: 'Payroll',
  time: 'Time & Attendance',
};

/** The row's modules; when the server sent none, read them off the places so the pills (and the
 * places in their card) still show */
export const getInviteModules = (invite: InviteListItem): string | string[] | null | undefined => {
  const hasModules = Array.isArray(invite.modules) ? invite.modules.length > 0 : !!invite.modules?.trim();
  if (hasModules || !invite.items?.length) {
    return invite.modules;
  }
  return Array.from(new Set(invite.items.map(item => ITEM_MODULE_NAMES[item.module]).filter(Boolean)));
};
